import { CreatePagerItemProps, PagerFactory, PagerRangeOptions } from './data-table-pager.types';

const createInnerPrevItem = (firstGroupEnd: number, innerGroupStart: number, pageFactory: PagerFactory) => {
    const prevPage = innerGroupStart - 1;
    const showEllipsis = prevPage !== firstGroupEnd + 1;

    return showEllipsis ? createEllipsisItem() : pageFactory(prevPage);
};

const createInnerNextItem = (innerGroupEnd: number, lastGroupStart: number, pageFactory: PagerFactory) => {
    const nextPage = innerGroupEnd + 1;
    const showEllipsis = nextPage !== lastGroupStart - 1;

    return showEllipsis ? createEllipsisItem() : pageFactory(nextPage);
};

const createSimpleRange = (start: number, end: number, pageFactory: PagerFactory) =>
    Array(end + 1 - start)
        .fill(undefined)
        .map((_, idx) => start + idx);

const createComplexRange = (options: PagerRangeOptions, pageFactory: PagerFactory) => {
    const { page, pageCount, boundaryRange, siblingRange } = options;

    const firstGroupEnd = boundaryRange;
    const firstGroup = createSimpleRange(1, firstGroupEnd, pageFactory);

    const lastGroupStart = pageCount + 1 - boundaryRange;
    const lastGroup = createSimpleRange(lastGroupStart, pageCount, pageFactory);

    const innerGroupStart = Math.min(
        Math.max(page - siblingRange, firstGroupEnd + 2),
        lastGroupStart - 1 - 2 * siblingRange - 1
    );
    const innerGroupEnd = innerGroupStart + 2 * siblingRange;
    const innerGroup = createSimpleRange(innerGroupStart, innerGroupEnd, pageFactory);

    return [
        ...firstGroup,
        createInnerPrevItem(firstGroupEnd, innerGroupStart, pageFactory),
        ...innerGroup,
        createInnerNextItem(innerGroupEnd, lastGroupStart, pageFactory),
        ...lastGroup,
    ].filter(Boolean);
};

const isSimplePagination = ({ boundaryRange, siblingRange, pageCount }: PagerRangeOptions): boolean => {
    const boundaryRangeSize = 2 * boundaryRange;
    const ellipsisSize = 2;
    const siblingRangeSize = 2 * siblingRange;

    return 1 + ellipsisSize + siblingRangeSize + boundaryRangeSize >= pageCount;
};

const createEllipsisItem = (): CreatePagerItemProps => ({
    Elem: 'span',
    buttonType: 'ellipsis',
    nextPage: 0,
});

const createPrevItem = (page: number): CreatePagerItemProps => ({
    Elem: 'button',
    active: false,
    disabled: page <= 1,
    buttonType: 'previous',
    nextPage: Math.max(1, page - 1),
});

const createNextItem = (page: number, totalPages: number): CreatePagerItemProps => ({
    active: false,
    disabled: page >= totalPages,
    buttonType: 'next',
    nextPage: Math.min(page + 1, totalPages),
});

const createPageFactory =
    (page: number): ((pageNumber: number) => CreatePagerItemProps) =>
    (nextPage: number) => ({
        active: page === nextPage,
        disabled: page === nextPage,
        buttonType: 'page',
        nextPage,
    });

export default function createPagerItems(options: Partial<PagerRangeOptions>) {
    const resolvedOptions = {
        ...{
            page: 1,
            pageCount: 1,
            boundaryRange: 1,
            siblingRange: 1,
        },
        ...options,
    };

    const { page, pageCount } = resolvedOptions;
    const pageFactory = createPageFactory(page);
    const innerRange = isSimplePagination(resolvedOptions)
        ? createSimpleRange(1, pageCount, pageFactory)
        : createComplexRange(resolvedOptions, pageFactory);

    return [createPrevItem(page), ...innerRange, createNextItem(page, pageCount)];
}
